import { useCallback, useEffect, useState } from 'react'



import { Tick02Icon } from '@hugeicons/react'
import { Cancel01Icon } from '@hugeicons/react'
import { useLocalStorage } from '@uidotdev/usehooks'
import { BottomSheet } from 'react-spring-bottom-sheet'

import { useAppRouter as useRouter } from '@/shared/hooks/useAppRouter'
import { useCheckIn } from '@/shared/hooks/useCheckIn'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { Button } from '@/shared/ui'
import { QrCodeReader } from '@/shared/ui/QrCodeReader'
import { useReloadGuard } from '@/shared/version'

export const CheckInWatcher = () => {
  const router = useRouter()
  const { basePathWithId } = useNavigation()
  const { check_in_uid: queryUid, check_in_code: queryCode } = router.query
  const [uid, setUid] = useLocalStorage('evnttz.check_in_uid', null)
  const [code, setCode] = useLocalStorage('evnttz.check_in_code', null)
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState(null)

  // Check-in em andamento segura o reload: recarregar no meio da confirmação
  // perderia a leitura e obrigaria o participante a refazer na fila.
  useReloadGuard(status === 'in_progress', 'check-in')
  const [tempCode, setTempCode] = useState(null)
  const { checkIn, extractDataFromUrl } = useCheckIn()

  const loadLink = useCallback(async () => {
    if (!uid || !code || status == 'loading') return
    setOpen(true)
    setStatus('loading')

    try {
      const { link, check } = await checkIn({ uid, code })

      if (!check) {
        handleError()
        return
      }

      if (link.kind === 'fixed') {
        setStatus('success')
        cleanup()
      } else setStatus('in_progress')
    } catch (error) {
      handleError()
    }
  }, [uid, code])

  const handleError = () => {
    setStatus('error')
    cleanup()
    setOpen(true)
  }

  const handleScanQrCode = async url => {
    const { uid: newUid, code: newCode } = extractDataFromUrl(url)
    if (!newUid || !newCode || tempCode == newCode) return
    setStatus('loading')

    try {
      setTempCode(newCode)

      const payload =
        code && code !== newCode
          ? { uid: newUid, code, confirmationCode: newCode }
          : { uid: newUid, code: newCode }

      const { link, check } = await checkIn(payload)

      if (!check) {
        handleError()
        return
      }

      if (link.kind === 'fixed' || code !== newCode) {
        setStatus('success')
        cleanup()
      } else {
        setCode(newCode)
        setStatus('in_progress')
      }
    } catch {
      handleError()
    }
  }

  const handleDismiss = () => {
    cleanup()
    setOpen(false)
  }

  const cleanup = () => {
    router.push(basePathWithId, undefined, { shallow: true })
    setUid(null)
    setCode(null)
  }

  useEffect(() => {
    if (queryUid && queryUid !== uid) setUid(queryUid)
  }, [queryUid, uid])

  useEffect(() => {
    if (queryCode && queryCode !== code) setCode(queryCode)
  }, [queryCode, code])

  useEffect(() => {
    loadLink()
  }, [loadLink])

  return (
    <BottomSheet
      open={open}
      onDismiss={handleDismiss}
      defaultSnap={({ snapPoints, lastSnap }) =>
        lastSnap ?? Math.max(...snapPoints)
      }
      snapPoints={({ maxHeight }) => [
        maxHeight - maxHeight / 5,
        maxHeight * 0.85
      ]}
      header={
        <h1 className="flex justify-center items-center text-xl font-bold text-gray-800">
          Auto Credenciamento
        </h1>
      }
      footer={
        <div className="flex gap-6">
          <Button
            type="button"
            variant="light"
            icon
            className="shrink-0 text-gray-900"
            leadingIcon={<Cancel01Icon size={20} variant="duotone" />}
            onClick={handleDismiss}
            aria-label="Fechar"
          />
          {status === 'success' ? (
            <Button
              type="button"
              variant="success"
              className="grow"
              onClick={handleDismiss}
              showTrailingIcon={false}
            >
              Fechar
            </Button>
          ) : (
            <span className="inline-flex grow items-center justify-center rounded-[0.625rem] bg-info px-4 text-sm font-semibold text-white">
              Mantenha a câmera apontada
            </span>
          )}
        </div>
      }
    >
      <div className="p-6">
        <div className="p-1 border-2 border border-gray-300 rounded">
          {status === 'success' && (
            <div className="overflow-hidden aspect-square rounded bg-gray-100">
              <Tick02Icon variant="solid" className="text-success" />
            </div>
          )}
          {status !== 'success' && open && (
            <QrCodeReader
              className="overflow-hidden aspect-square rounded"
              onSuccess={handleScanQrCode}
              disabled={status === 'loading' || status === 'success'}
            />
          )}
        </div>
        {status === 'error' && (
          <div className="mt-3 rounded-md bg-danger-light text-danger font-semibold text-sm px-4 py-3">
            Ocorreu um erro, tente novamente apontando a câmera para o QR Code.
          </div>
        )}
        {status === 'success' && (
          <div className="mt-3 rounded-md bg-success-light text-success font-semibold text-sm px-4 py-3">
            Credenciamento realizado com sucesso!
          </div>
        )}
      </div>
    </BottomSheet>
  )
}
